import React, {
  forwardRef,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  AutocompleteRenderInputParams,
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { device } from '../../constants/devices';
import SearchImg from '../../assets/img/lupe.svg';
import FilterIcon from '../../assets/img/filterLight.svg';
import AutocompleteStyled from '../autocomplete/styled';
import { DropDownStyled } from '../dropdown/styled';
import theme from '../../constants/theme';
import { useIsWhichSize } from '../../hooks/useIsWhichSize';
import { useFilterHeaderUpdate } from '../../utils/filterUpdate';
import { scrollIntoView } from '../../utils/scrollIntoView';
import { t } from '../../utils/translate';
import { FilterHeaderInputValues, FilterHeaderProps } from './props';
import MyAPIConnector from '../../connector/CustomConnector';
import { FilterContext } from '../../context/filter';
import { Loader } from '@googlemaps/js-api-loader';
import { GoogleMapsCookie } from '../googleMapsCookie';

const apiConnector = new MyAPIConnector();

export const FilterHeader = ({
  refResults,
  radius,
  setRadius,
  geocode,
  setGeocode,
}: FilterHeaderProps) => {
  const { isMobile, isSmallerScreen } = useIsWhichSize();
  const gridRef = useRef<HTMLDivElement>(null);
  const locationBoxRef = useRef<HTMLDivElement>(null);
  const [filterWidth, setFilterWidth] = useState(0);
  const [locationBoxRefWidth, setLocationBoxRef] = useState(0);
  const [options, setOptions] = useState<Array<string>>([]);
  const [termOptions, setTermOptions] = useState<Array<any>>([]);
  const [termInput, setTermInput] = useState<string>('');
  const [isFocusedTerm, setIsFocusedTerm] = useState(false);
  const [isFocusedLocation, setIsFocusedLocation] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showGoogleCookie, setShowGoogleCookie] = useState(true);
  const [googleServiceId, setGoogleServiceId] = useState('');
  const [locationURL, setLocationURL] = useState<FilterHeaderInputValues>(
    {} as FilterHeaderInputValues
  );
  const context = useContext(FilterContext);
  const { term, setTerm, location, setLocation } = context;

  const isLoaded = useRef<boolean>(false);
  const autocompleteService = useRef<any>(null);
  const geocoder = useRef<any>(null);

  const setFilter = (window as any).setFilter;
  const removeFilter = (window as any).removeFilter;
  const setSearchTerm = (window as any).setSearchTerm;

  const items = [
    { name: '10 km', value: '10' },
    { name: '20 km', value: '20' },
    { name: '30 km', value: '30' },
    { name: '50 km', value: '50' },
  ];

  const ListboxComponent = forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLElement>
  >((props, ref) => {
    return (
      <Box ref={ref}>
        <Typography
          fontSize="18px"
          color="primary"
          fontWeight={700}
          sx={{ padding: '20px 20px 0 16px' }}
        >
          {/* @ts-ignore */}
          {props.children?.length}{' '}
          {props.id === 'term-listbox' ? 'Stellenangebote' : 'Orte'}
        </Typography>
        <ul {...props} />
      </Box>
    );
  });

  const fetchTerms = async (text: string) => {
    apiConnector
      .onAutocomplete({ searchTerm: text }, { suggestions: {} })
      .then((data: any) => setTermOptions(data || []));
  };

  const fetchLocations = (text: string) => {
    if (text.length >= 1) {
      autocompleteService.current.getPlacePredictions(
        {
          input: text,
          componentRestrictions: { country: 'DE' },
          types: ['postal_code', 'locality', 'administrative_area_level_3'],
        },
        (predictions: any, status: any) => {
          switch (status) {
            case (window as any).google.maps.places.PlacesServiceStatus.OK:
              setOptions(
                predictions ? predictions.map((p: any) => p.description) : []
              );
              return;
            case (window as any).google.maps.places.PlacesServiceStatus
              .ZERO_RESULTS:
              setOptions([]);
              return;
            default:
              console.error('Error fetching autocomplete predictions', status);
              return;
          }
        }
      );
    } else {
      setOptions([]);
    }
  };

  const changeRadius = (value: string) => {
    setRadius((prevState) => ({
      ...prevState,
      value,
    }));
  };

  const setFilterLocationGeopoint = () => {
    setFilter('location_geopoint', {
      from: 0,
      to: Number((window as any).search_radius),
      unit: 'km',
      center: [(window as any).lat, (window as any).lng].join(', '),
    });
  };

  const removeLocationFilter = () => {
    removeFilter('search_location');
    removeFilter('location_geopoint');
  };

  const fetchGeocode = (value: string, setIntoWindow?: boolean) => {
    if (!value) return setGeocode({ lat: 0, lng: 0 });

    geocoder.current.geocode(
      { address: value },
      (results: any, status: any) => {
        if (
          !!results &&
          status === (window as any).google.maps.GeocoderStatus.OK
        ) {
          const location = results[0].geometry.location;
          setGeocode({ lat: location.lat(), lng: location.lng() });

          if (setIntoWindow) {
            (window as any).lat = location.lat();
            (window as any).lng = location.lng();

            if ((window as any).search_radius) {
              setFilterLocationGeopoint();
            } else {
              removeFilter('location_geopoint');
            }
          }
        } else {
          console.log('Error getting geocode', status);
        }
      }
    );
  };

  const handleButtonClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    (window as any).lat = geocode.lat;
    (window as any).lng = geocode.lng;
    (window as any).search_radius = radius.value;
    (window as any).search_location = location.value;
    let newTerm = typeof term === 'string' ? term : term.value;

    if ((window as any).showInitiative && !newTerm) {
      newTerm = '';
    }

    setSearchTerm(getNewTermValue(newTerm), {
      refresh: false,
    });
    setFilter('search_location', [(window as any).search_location], 'all');
    setFilter('search_radius', [(window as any).search_radius], 'all');

    (window as any).lat ? setFilterLocationGeopoint() : removeLocationFilter();
    (window as any).showInitiative = false;

    if (isMobile) {
      scrollIntoView(refResults);
    }
  };

  const selectTopLabel = (hasText: boolean): string => {
    if (isSmallerScreen && !hasText) {
      return '-12px';
    }

    if (!isSmallerScreen && !hasText) {
      return '0';
    }

    return '12px';
  };

  const getLabelProps = (hasText: boolean) => {
    return {
      style: {
        fontSize: isSmallerScreen && hasText ? '6px' : '16px',
        top: selectTopLabel(hasText),
        color: hasText
          ? theme.palette.primary.light
          : theme.palette.secondary.main,
      },
    };
  };

  const getNewTermValue = (value: any) => {
    let newValue = '';

    if (typeof value === 'string') {
      newValue = value;
    }

    if (value?.suggestion) {
      newValue = value.suggestion;
    }

    return newValue;
  };

  const loadGoogleAPI = (location: any) => {
    if (!isLoaded.current) {
      isLoaded.current = true;
      const loader = new Loader({
        apiKey: process.env.REACT_APP_GOOGLE_API_KEY!,
        libraries: ['places'],
        language: 'de',
      });

      loader.load().then(({ maps }) => {
        autocompleteService.current = new maps.places.AutocompleteService();
        geocoder.current = new maps.Geocoder();

        setLocation(location);
        fetchGeocode(location?.value);
      });
    }
  };

  const getGoogleCookieConsent = (location: any) => {
    const intervalId = setInterval(() => {      
    const services = (window as any).UC_UI?.getServicesBaseInfo();
    
          
      const googleService = services.find((s: any) => s.name === 'Google Maps');      
      const consent = googleService?.consent?.status;

      setShowGoogleCookie(!consent);
      setGoogleServiceId(googleService?.id);

      if (consent) {
        loadGoogleAPI(location);
      } else {
        removeLocationFilter();
      }
      
      clearGoogleConsentInterval(intervalId);    
    },500);
  };

  const clearGoogleConsentInterval = (intervalId: any) => {    
    clearInterval(intervalId);
  }

  const handleResize = () => {
    if (gridRef.current) {
      const { width } = gridRef.current.getBoundingClientRect();

      setFilterWidth(width);
    }

    if (locationBoxRef.current) {
      const { width } = locationBoxRef.current.getBoundingClientRect();

      setLocationBoxRef(width);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (termInput.length >= 3) {
        fetchTerms(termInput);
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [termInput]);

  useEffect(() => {
    if ((window as any).showInitiative) {
      setSearchTerm('initiativ');
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useFilterHeaderUpdate(
    location.isOnInit,
    'search_location',
    (patchLocation) => {
      getGoogleCookieConsent(patchLocation);
      setLocationURL(patchLocation);
    }
  );
  useFilterHeaderUpdate(term.isOnInit, 'q', setTerm);
  useFilterHeaderUpdate(
    radius.isOnInit || false,
    'search_radius',
    (patch: SetStateAction<FilterHeaderInputValues>) => {
      // @ts-ignore
      setRadius({ isOnInit: false, value: patch.value || '20' });
    }
  );

  return (
    <form onSubmit={handleButtonClick}>
      <Grid
        ref={gridRef}
        container
        spacing={2}
        className="sui-search-box__wrapper"
      >
        <Grid item sm={12} lg>
          <AutocompleteStyled
            freeSolo
            autoSelect
            id="term"
            value={term.value || ''}
            onInputChange={(_: any, value: string) => {
              if (value === '') {
                setTermOptions([]);
              }

              setTermInput(value);
            }}
            onChange={(event: any, value: any) => {
              setTerm((prevState) => ({
                ...prevState,
                value: getNewTermValue(value),
              }));
            }}
            options={termOptions || []}
            filterOptions={(options) => options}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextField
                {...params}
                onFocus={() => setIsFocusedTerm(true)}
                onBlur={() => setIsFocusedTerm(false)}
                inputProps={{
                  ...params.inputProps,
                  value: params.inputProps.value || term.value || '',
                }}
                InputLabelProps={getLabelProps(
                  Boolean(params.inputProps.value || term.value)
                )}
                placeholder={
                  isSmallerScreen && !isFocusedTerm
                    ? t('Nach Stelle oder Schlagwort suchen')
                    : ''
                }
                label={
                  !isSmallerScreen
                    ? t('Nach Stelle oder Schlagwort suchen')
                    : ''
                }
                sx={{ fontWeight: 600 }}
              />
            )}
            getOptionLabel={(option: any) =>
              typeof option === 'string' ? option : option.suggestion
            }
            renderOption={(props: object, option: any) => (
              <Box
                {...props}
                key={option.suggestion}
                display="flex"
                flexDirection="column"
                alignItems="flex-start !important"
                padding="20px 20px 14px 20px !important"
              >
                <Typography variant="h3" fontWeight={500}>
                  {option.suggestion}
                </Typography>
              </Box>
            )}
            sx={{
              '.MuiAutocomplete-input': {
                padding: '22px 0 0 5px !important',
                [device.small]: {
                  padding: '0 0 0 5px !important',
                },
              },
            }}
            componentsProps={{
              paper: {
                sx: {
                  '@media screen and (max-width: 1200px)': {
                    display: 'none',
                  },
                  width: `calc(${filterWidth}px - 16px)`,
                  border: termOptions?.length
                    ? `5px solid ${theme.palette.primary.light}`
                    : 'unset',
                },
              },
            }}
          />
        </Grid>

        <Grid item sm={12} lg>
          <Box
            ref={locationBoxRef}
            display="flex"
            position="relative"
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              setAnchorEl(event.currentTarget);
            }}
          >
            {showGoogleCookie && (
              <Box
                position="absolute"
                top={0}
                right={0}
                left={0}
                bottom={0}
                zIndex={1}
                sx={{
                  background: 'transparent',
                  cursor: 'pointer',
                }}
                onClick={() => locationBoxRef.current?.click()}
              ></Box>
            )}

            <Box
              sx={{
                height: '64px',
                width: '100%',
                position: 'relative',
                [device.small]: {
                  maxWidth: 'calc(100% - 132px)',
                  height: '4px',
                },
              }}
            >
              <AutocompleteStyled
                freeSolo
                autoSelect
                id="location-search"
                value={location.value || ''}
                onInputChange={(_: any, value: string) => {
                  fetchLocations(value);
                }}
                onChange={(event: any, value: any) => {
                  setLocation((prevState) => ({
                    ...prevState,
                    value,
                  }));
                  fetchGeocode(value);
                }}
                options={options || []}
                filterOptions={(options) => options}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    onFocus={() => setIsFocusedLocation(true)}
                    onBlur={() => setIsFocusedLocation(false)}
                    inputProps={{
                      ...params.inputProps,
                      value: params.inputProps.value || location.value || '',
                    }}
                    InputLabelProps={getLabelProps(
                      Boolean(params.inputProps.value || location.value)
                    )}
                    placeholder={
                      isSmallerScreen && !isFocusedLocation
                        ? t('Nach Ort suchen')
                        : ''
                    }
                    label={
                      !isSmallerScreen
                        ? t('Nach Postleitzahl oder Ort suchen')
                        : ''
                    }
                    sx={{ fontWeight: 600 }}
                  />
                )}
                ListboxComponent={ListboxComponent}
                onBlur={() => setOptions([])}
                sx={{
                  borderRight: 'none',
                  minWidth: '30px',
                  '.MuiAutocomplete-input': {
                    padding: '22px 0 0 5px !important',
                    [device.small]: {
                      padding: '0 0 0 5px !important',
                    },
                  },
                }}
                componentsProps={{
                  paper: {
                    sx: {
                      width: 'calc(100% + 136px)',
                      border: options?.length
                        ? `5px solid ${theme.palette.primary.light}`
                        : 'unset',
                    },
                  },
                }}
              />
            </Box>

            <Box>
              <DropDownStyled variant="standard" sx={{ width: '132px' }}>
                <Select
                  value={radius.value}
                  onChange={(e: SelectChangeEvent<string>) =>
                    changeRadius(e.target.value)
                  }
                  displayEmpty
                  renderValue={radius.value !== '' ? undefined : () => '20 km'}
                  IconComponent={(props: any) => (
                    <KeyboardArrowDown {...props} />
                  )}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '.MuiMenuItem-root:hover': {
                          backgroundColor: 'unset',
                          color: theme.palette.secondary.light,
                        },
                        '& .MuiMenuItem-root': {
                          color: theme.palette.secondary.main,
                          background: 'unset !important',
                        },
                        '.Mui-selected': {
                          backgroundColor: 'unset !important',
                        },
                        border: `5px solid ${theme.palette.primary.light}`,
                        marginTop: '11px',
                      },
                    },
                  }}
                  sx={{
                    h6: {
                      fontSize: '16px',
                    },
                  }}
                >
                  {items &&
                    items.map((item, index) => (
                      <MenuItem value={item.value} key={index}>
                        <Typography
                          fontWeight={600}
                          sx={{
                            fontSize: '16px',
                          }}
                        >
                          {item.name}
                        </Typography>
                      </MenuItem>
                    ))}
                </Select>
              </DropDownStyled>
            </Box>
          </Box>

          {showGoogleCookie && (
            <GoogleMapsCookie
              width={locationBoxRefWidth}
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              onClick={() => {
                (window as any).UC_UI?.acceptService(googleServiceId).then(
                  () => {
                    loadGoogleAPI(locationURL);
                    setShowGoogleCookie(false);
                  }
                );
              }}
            />
          )}
        </Grid>

        <Grid
          item
          lg
          sm={12}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ [device.large]: { maxWidth: '196px' } }}
        >
          <Button className="btn-search" type="submit">
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ marginRight: '8px', marginTop: '1px' }}
            >
              {!isSmallerScreen ? t('Stelle suchen') : t('Suchen')}
            </Typography>

            <img src={SearchImg} alt={t('Stellen filtern')} />
          </Button>

          {isMobile ? (
            <Box
              display="flex"
              gap={1}
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                let element =
                  document.getElementsByClassName('sui-layout-sidebar')[0];
                (element as HTMLElement).style.display = 'block';
              }}
            >
              <img src={FilterIcon} alt={t('Stellen filtern')} />
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  textDecoration: 'underline',
                  color: theme.palette.primary.light,
                  letterSpacing: '0.28px',
                }}
              >
                {t('Filtern')}
              </Typography>
            </Box>
          ) : null}
        </Grid>
      </Grid>
    </form>
  );
};
